import { SagaIterator } from 'redux-saga';
import { call, delay, put } from 'redux-saga/effects';
import { DevBoxHyperVProvisioningState } from '../../../constants/add-ons';
import { getDevBoxAddOn, GetDevBoxHyperVResponse } from '../../../data/services/data-plane-api/add-ons';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import {
    pollNonTerminalDevBoxHyperV,
    pollNonTerminalDevBoxHyperVComplete,
    pollNonTerminalDevBoxHyperVError,
} from '../../actions/add-ons/hyper-v-action-creators';
import { PollNonTerminalDevBoxHyperVAction } from '../../actions/add-ons/hyper-v-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeEvery } from '../../effects/take';
import { getPollingInitialDelayMs, getPollingIntervalMs } from '../../utilities/poll-action';

export function* pollNonTerminalDevBoxHyperVSaga(action: PollNonTerminalDevBoxHyperVAction): SagaIterator {
    let shouldPoll = true;
    const { meta, payload } = action;
    const { activityId } = meta ?? {};
    const { id, name } = payload;

    try {
        yield delay(getPollingInitialDelayMs(action));

        const pollingInterval = getPollingIntervalMs(action);

        while (shouldPoll) {
            const accessToken: string = yield putAndAwait(
                getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
            );
            const response: GetDevBoxHyperVResponse = yield call(getDevBoxAddOn, id, name, accessToken, activityId);

            if (isFailureResponse(response)) {
                yield put(pollNonTerminalDevBoxHyperVComplete({ id }, meta));
                yield resolveAction(action, response);
                return;
            }

            const { data } = response;
            const { provisioningState } = data;

            shouldPoll = provisioningState === DevBoxHyperVProvisioningState.Creating;

            if (shouldPoll) {
                yield delay(pollingInterval);
            } else {
                yield put(pollNonTerminalDevBoxHyperVComplete({ id, result: data }, meta));
                yield resolveAction(action, response);
            }
        }
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.PollNonTerminalDevBoxHyperV);
        yield put(pollNonTerminalDevBoxHyperVError({ error, id }, meta));
        yield rejectAction(action, error);
    }
}

export function* pollNonTerminalDevBoxHyperVListenerSaga(): SagaIterator {
    yield takeEvery(pollNonTerminalDevBoxHyperV, pollNonTerminalDevBoxHyperVSaga);
}
